import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Label,
} from 'recharts';
import { formatDuration } from 'src/services/coCustomerAnalyticsService';

export const AnalyticsAgentLineChart = ({
  dataKey = '',
  data = [],
  dateRange = 'day',
  timeAxis = 'seconds',
  color = '#65D5DC',
  isByUser = false,
  userColors,
}) => {
  const isDayRange = dateRange === 'day';
  const userNames = [];
  let chartData = [];

  if (isByUser) {
    chartData = data.map((dataItem) => {
      return Object.keys(dataItem).reduce((previousValue, currentValue) => {
        if (currentValue === 'date') {
          return {
            ...previousValue,
            [currentValue]: dataItem[currentValue],
          };
        }

        if (!userNames.includes(currentValue)) {
          userNames.push(currentValue);
        }

        return {
          ...previousValue,
          [currentValue]: dataItem[currentValue][dataKey] || 0,
        };
      }, {});
    });
  } else {
    const isEmpty = data.filter((item) => Boolean(item[dataKey])).length === 0;

    chartData = isEmpty ? [] : data;
  }
  const tooltipFormatter = (value) => {
    return value ? formatDuration(value, timeAxis as any) : 0;
  };

  const isSingleDot =
    chartData.filter((charDataItem) => {
      return isByUser ? Object.keys(charDataItem).length > 1 : charDataItem[dataKey];
    }).length <= 1;

  return Boolean(chartData.length) ? (
    <ResponsiveContainer
      width="100%"
      height="100%"
      minHeight={'500px'}
      className={'bg-white rounded-sm-5'}
    >
      <LineChart
        width={500}
        height={300}
        data={chartData}
        margin={{
          top: 50,
          right: 50,
          left: 10,
          bottom: 50,
        }}
      >
        <CartesianGrid vertical={false} strokeDasharray="1 1" />
        <XAxis dataKey="date">
          <Label
            value={isDayRange ? 'Days' : 'Hours'}
            offset={-10}
            position="insideBottomRight"
            className={'fill-ebony text-sm font-semibold'}
          />
        </XAxis>
        <YAxis>
          <Label
            value={timeAxis}
            position="insideTopLeft"
            className={'fill-ebony text-sm font-semibold'}
            content={({ value }) => {
              return (
                <text
                  offset="0"
                  x="10"
                  y="20"
                  className="recharts-text fill-ebony text-sm font-semibold capitalize"
                  textAnchor="middle"
                  fill="#808080"
                >
                  <tspan x={60} dy="0.71em">
                    {value}
                  </tspan>
                </text>
              );
            }}
          />
        </YAxis>
        <Tooltip
          formatter={tooltipFormatter}
          labelStyle={{
            color: '#FFF',
            fontFamily: 'Bai Jamjuree',
            fontSize: '14px',
          }}
          contentStyle={{
            borderRadius: '5px',
            background: '#05050D',
          }}
          itemStyle={{
            fontFamily: 'Bai Jamjuree',
            fontSize: '12px',
            fontStyle: 'normal',
            fontWeight: 600,
          }}
        />
        {Boolean(data.length) && (
          <ReferenceLine
            x={data[parseInt((data.length / 2).toFixed(0)) - 1]['date']}
            stroke="#CCCCCC"
            strokeDasharray="1 1"
          />
        )}
        {isByUser ? (
          userNames.map((userName) => {
            const stroke = userColors ? userColors[userName] : color;

            return (
              <Line
                type="linear"
                key={userName}
                dataKey={userName}
                name={userName}
                stroke={stroke}
                strokeWidth={3}
                connectNulls={true}
                dot={isSingleDot ? { stroke, strokeWidth: 10 } : false}
              />
            );
          })
        ) : (
          <Line
            type="linear"
            dataKey={dataKey}
            name={dataKey}
            stroke={color}
            strokeWidth={3}
            fontSize={'10px'}
            connectNulls={true}
            dot={isSingleDot ? { stroke: color, strokeWidth: 10 } : false}
          />
        )}
      </LineChart>
    </ResponsiveContainer>
  ) : (
    <div
      className={
        'min-h-[450px] bg-white rounded-sm-5 flex flex-col items-center justify-center font-medium italic text-ebony_opacity_50 text-lg'
      }
    >
      <p>No data available for the selected period</p>
    </div>
  );
};
